import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"
import { i18n } from "../lib/i18n"

/**
 * Stimulus controller powering the Model & Map mapping-review UI (v3 schema).
 *
 * Responsibilities:
 *  - expand/collapse the file-card body on a toggle click
 *  - expand/collapse a field's reasoning panel on a toggle click
 *  - swap the transform-args form when the transform `<select>` changes
 *    so edits are bounded by the selected transform's args_schema
 *  - swap the field-source editor when the source mode `<select>` changes
 *    (column / from_step / from_split / sources)
 *  - drag-reorder the file load-sequence list (file-level reorder)
 *  - drag-reorder the ordered steps inside a single file_mapping
 *
 * Values:
 *  - transformsValue: JSON array of { name, args_schema, description } —
 *    the catalog for the transform args swap + description hover
 */
interface TransformSpec {
  name: string
  args_schema: Record<string, unknown>
  description?: string
}

type ArgType = "boolean" | "integer" | "number" | "array" | "string" | "hash" | string

export default class ImportMappingController extends Controller<HTMLElement> {
  static targets = [
    "fileCard",
    "cardBody",
    "transformSelect",
    "reasoningPanel",
    "order",
    "stepList",
    "stepCard",
    "sourceModeSelect",
    "fieldRow",
  ]
  static values = {
    transforms: { type: String, default: "[]" },
  }

  declare readonly fileCardTargets: HTMLElement[]
  declare readonly cardBodyTargets: HTMLElement[]
  declare readonly transformSelectTargets: HTMLSelectElement[]
  declare readonly reasoningPanelTargets: HTMLElement[]
  declare readonly hasOrderTarget: boolean
  declare readonly orderTarget: HTMLElement
  declare readonly stepListTargets: HTMLElement[]
  declare readonly stepCardTargets: HTMLElement[]
  declare readonly sourceModeSelectTargets: HTMLSelectElement[]
  declare readonly fieldRowTargets: HTMLElement[]

  declare transformsValue: string

  private parsedTransforms: TransformSpec[] = []
  private draggingSourceFile: string | null = null
  private draggingStepId: string | null = null
  private draggingStepList: HTMLElement | null = null

  connect(): void {
    this.parsedTransforms = this.parseTransforms()
  }

  // --- Expand/collapse -----------------------------------------------------

  toggleCard(event: Event): void {
    event.preventDefault()
    const button = event.currentTarget as HTMLElement
    const card = button.closest("[data-import-mapping-target='fileCard']") as HTMLElement | null
    if (!card) return

    const body = card.querySelector(
      "[data-import-mapping-target='cardBody']"
    ) as HTMLElement | null
    if (!body) return

    const expanded = button.getAttribute("aria-expanded") === "true"
    body.classList.toggle("hidden", expanded)
    button.setAttribute("aria-expanded", expanded ? "false" : "true")

    const icon = button.querySelector<HTMLElement>("[data-import-mapping-toggle-icon]")
    if (icon) icon.textContent = expanded ? "▸" : "▾"

    const label = button.querySelector<HTMLElement>("[data-import-mapping-toggle-label]")
    if (label) {
      label.textContent = expanded
        ? (button.dataset.collapsedLabel ?? "")
        : (button.dataset.expandedLabel ?? "")
    }
  }

  toggleReasoning(event: Event): void {
    event.preventDefault()
    const button = event.currentTarget as HTMLElement
    const row = button.closest("tr")
    if (!row) return

    const panel = row.querySelector(
      "[data-import-mapping-target='reasoningPanel']"
    ) as HTMLElement | null
    if (!panel) return

    const expanded = button.getAttribute("aria-expanded") === "true"
    panel.classList.toggle("hidden", expanded)
    button.setAttribute("aria-expanded", expanded ? "false" : "true")
  }

  addValueMapRow(event: Event): void {
    event.preventDefault()
    const button = event.currentTarget as HTMLElement
    const form = button.closest("form")
    if (!form) return

    const rows = form.querySelector<HTMLElement>(
      "[data-import-mapping-target='valueMapRows']"
    )
    const template = form.querySelector<HTMLTemplateElement>(
      "template[data-import-mapping-target='valueMapTemplate']"
    )
    if (!rows || !template) return

    rows.insertAdjacentHTML("beforeend", template.innerHTML.trim())
  }

  removeValueMapRow(event: Event): void {
    event.preventDefault()
    const button = event.currentTarget as HTMLElement
    button.closest("tr")?.remove()
  }

  unknownPolicyChanged(event: Event): void {
    const input = event.currentTarget as HTMLInputElement
    const form = input.closest("form")
    if (!form) return

    const showDefaultTo = input.value === "default_to"
    form
      .querySelector<HTMLElement>("[data-import-mapping-target='defaultToContainer']")
      ?.classList.toggle("hidden", !showDefaultTo)
    form
      .querySelector<HTMLElement>("[data-import-mapping-target='failWarning']")
      ?.classList.toggle("hidden", input.value !== "fail")
  }

  // --- Source-mode swap ----------------------------------------------------

  sourceModeChanged(_event: Event): void {
    // Form submission writes the new mode; the server re-renders the row
    // with the correct source-editor variant. Client-side we just let the
    // form submit (handled by onchange in the template).
  }

  // --- Transform args swap -------------------------------------------------

  transformChanged(event: Event): void {
    const select = event.currentTarget as HTMLSelectElement
    const row = select.closest("tr") || select.closest("form")
    if (!row) return

    const name = select.value
    const spec = this.parsedTransforms.find((t) => t.name === name)

    // Surface the description as a tooltip on the select itself.
    select.title = spec?.description ?? ""

    let container = row.querySelector<HTMLElement>(".transform-args-container")
    if (!container) {
      container = document.createElement("div")
      container.className = "transform-args-container mt-1 flex flex-col gap-1"
      select.parentElement?.appendChild(container)
    }
    // Remember existing values so users don't lose in-flight edits when
    // the schema changes but overlapping keys stay.
    const existing: Record<string, string> = {}
    container
      .querySelectorAll<HTMLInputElement>("input[data-arg-key]")
      .forEach((input) => {
        const key = input.dataset.argKey
        if (key) existing[key] = input.value
      })
    container.innerHTML = ""

    if (!spec || !spec.args_schema || Object.keys(spec.args_schema).length === 0) {
      return
    }

    for (const [key, schema] of Object.entries(spec.args_schema)) {
      const type = this.argType(schema)
      const label = document.createElement("label")
      label.className = "text-xs text-gray-600"
      label.textContent = i18n.t("import_mapping.arg_label", { name: key })
      container.appendChild(label)

      const wrapper = this.buildArgInput(key, type, existing[key])
      container.appendChild(wrapper)
    }
  }

  private argType(schema: unknown): ArgType {
    if (typeof schema === "string") return schema
    if (schema && typeof schema === "object") {
      const t = (schema as Record<string, unknown>).type ?? (schema as Record<string, unknown>)["type"]
      if (typeof t === "string") return t
    }
    return "string"
  }

  private buildArgInput(key: string, type: ArgType, existingValue?: string): HTMLElement {
    const inputName = `field[transform_args][${key}]`
    const typeName = `field[transform_arg_types][${key}]`
    const wrapper = document.createElement("div")
    const typeInput = document.createElement("input")
    typeInput.type = "hidden"
    typeInput.name = typeName
    typeInput.value = type
    wrapper.appendChild(typeInput)

    if (type === "boolean") {
      const hidden = document.createElement("input")
      hidden.type = "hidden"
      hidden.name = inputName
      hidden.value = "false"
      const checkbox = document.createElement("input")
      checkbox.type = "checkbox"
      checkbox.name = inputName
      checkbox.value = "true"
      checkbox.className = "checkbox checkbox-sm"
      checkbox.dataset.argKey = key
      checkbox.checked = existingValue === "true"
      wrapper.appendChild(hidden)
      wrapper.appendChild(checkbox)
      return wrapper
    }

    let input: HTMLInputElement | HTMLTextAreaElement
    if (type === "integer" || type === "number") {
      input = document.createElement("input")
      input.type = "number"
      input.className = "input input-bordered input-xs w-32"
    } else if (type === "array") {
      input = document.createElement("input")
      input.type = "text"
      input.className = "input input-bordered input-xs w-48 font-mono"
      input.placeholder = i18n.t("import_mapping.array_placeholder")
    } else if (type === "hash") {
      const textarea = document.createElement("textarea")
      textarea.className = "textarea textarea-bordered textarea-xs w-64 font-mono"
      textarea.rows = 3
      input = textarea
    } else {
      input = document.createElement("input")
      input.type = "text"
      input.className = "input input-bordered input-xs font-mono"
    }
    input.name = inputName
    input.dataset.argKey = key
    if (existingValue) input.value = existingValue
    wrapper.appendChild(input)
    return wrapper
  }

  // --- Drag-reorder: file-level -------------------------------------------

  orderDragStart(event: DragEvent): void {
    const li = event.currentTarget as HTMLElement
    this.draggingSourceFile = li.dataset.sourceFile ?? null
    event.dataTransfer?.setData("text/plain", this.draggingSourceFile ?? "")
  }

  orderDragOver(event: DragEvent): void {
    event.preventDefault()
    const li = event.currentTarget as HTMLElement
    if (!this.draggingSourceFile) return
    const dragged = this.orderTarget.querySelector<HTMLElement>(
      `[data-source-file='${this.draggingSourceFile}']`
    )
    if (!dragged || dragged === li) return
    const rect = li.getBoundingClientRect()
    const before = event.clientY < rect.top + rect.height / 2
    if (before) {
      li.parentElement?.insertBefore(dragged, li)
    } else {
      li.parentElement?.insertBefore(dragged, li.nextSibling)
    }
  }

  /**
   * Commits the new file order by POSTing to the server endpoint.
   * Server does the authoritative cycle-check; client refuses to submit
   * if it detects an obvious violation first.
   */
  async reorderDropped(event: DragEvent): Promise<void> {
    event.preventDefault()
    if (!this.hasOrderTarget) return

    const url = this.orderTarget.dataset.reorderUrl
    if (!url) return

    const items = Array.from(
      this.orderTarget.querySelectorAll<HTMLElement>("[data-source-file]")
    )
    const order = items.map((li) => li.dataset.sourceFile ?? "")

    if (this.detectClientCycle(order)) {
      this.showCycleError()
      return
    }

    await this.postOrder(url, "order[]", order)
  }

  // --- Drag-reorder: step-level -------------------------------------------

  stepDragStart(event: DragEvent): void {
    const card = event.currentTarget as HTMLElement
    this.draggingStepId = card.dataset.stepId ?? null
    this.draggingStepList = card.closest(
      "[data-import-mapping-target='stepList']"
    ) as HTMLElement | null
    event.dataTransfer?.setData("text/plain", this.draggingStepId ?? "")
  }

  stepDragOver(event: DragEvent): void {
    event.preventDefault()
    const overCard = (event.currentTarget as HTMLElement).closest(
      "[data-import-mapping-target='stepCard']"
    ) as HTMLElement | null
    if (!overCard || !this.draggingStepId || !this.draggingStepList) return
    const dragged = this.draggingStepList.querySelector<HTMLElement>(
      `[data-step-id='${this.draggingStepId}']`
    )
    if (!dragged || dragged === overCard) return
    const rect = overCard.getBoundingClientRect()
    const before = event.clientY < rect.top + rect.height / 2
    if (before) {
      overCard.parentElement?.insertBefore(dragged, overCard)
    } else {
      overCard.parentElement?.insertBefore(dragged, overCard.nextSibling)
    }
  }

  async stepDropped(event: DragEvent): Promise<void> {
    event.preventDefault()
    if (!this.draggingStepList) return

    const url = this.draggingStepList.dataset.reorderStepsUrl
    const fileIndex = this.draggingStepList.dataset.fileIndex
    if (!url || fileIndex === undefined) return

    const order = Array.from(
      this.draggingStepList.querySelectorAll<HTMLElement>(
        "[data-import-mapping-target='stepCard']"
      )
    ).map((el) => el.dataset.stepId ?? "")

    await this.postOrder(url, "order[]", order, { file_index: fileIndex })
    this.draggingStepId = null
    this.draggingStepList = null
  }

  /**
   * Public hook used by tests: given a desired order and a parallel
   * dependency map, return true iff the order would place any file
   * before a file it depends on (i.e., cycle candidate).
   */
  detectCycle(
    order: string[],
    dependencies: Record<string, string[]>
  ): boolean {
    const pos: Record<string, number> = {}
    order.forEach((name, i) => {
      pos[name] = i
    })
    for (const [src, deps] of Object.entries(dependencies)) {
      for (const dep of deps) {
        if (pos[src] !== undefined && pos[dep] !== undefined && pos[src] < pos[dep]) {
          return true
        }
      }
    }
    return false
  }

  private detectClientCycle(order: string[]): boolean {
    const raw = this.orderTarget.dataset.dependencies
    if (!raw) return false
    try {
      const deps = JSON.parse(raw) as Record<string, string[]>
      return this.detectCycle(order, deps)
    } catch {
      return false
    }
  }

  private showCycleError(): void {
    const existing = document.getElementById("mapping-review-validation")
    if (!existing) return
    existing.innerHTML = `<div class="alert alert-error">${i18n.t(
      "import_mapping.cycle_detected"
    )}</div>`
  }

  private async postOrder(
    url: string,
    orderKey: string,
    order: string[],
    extra: Record<string, string> = {}
  ): Promise<void> {
    const form = new FormData()
    order.forEach((f) => form.append(orderKey, f))
    for (const [k, v] of Object.entries(extra)) form.append(k, v)
    form.append("_method", "patch")
    const csrf = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content")
    if (csrf) form.append("authenticity_token", csrf)

    const response = await fetch(url, {
      method: "POST",
      body: form,
      headers: {
        Accept: "text/vnd.turbo-stream.html",
      },
    })
    if (!response.ok) return

    const html = await response.text()
    // Turbo only executes Stream actions when the HTML is fed to
    // `Turbo.renderStreamMessage`. Dispatching a synthetic
    // `turbo:before-stream-render` event alone does not parse or apply
    // the actions, so the server's `turbo_stream.redirect_to` on
    // success and cycle/mismatch error replacements would silently
    // no-op — leaving the client in an optimistic state out of sync
    // with the persisted config.
    Turbo.renderStreamMessage(html)
  }

  private parseTransforms(): TransformSpec[] {
    try {
      return JSON.parse(this.transformsValue) as TransformSpec[]
    } catch {
      return []
    }
  }
}
